import React, { useMemo } from 'react'
import { hapticTick } from '../../../../../_utils/haptics'
import { CAMPAIGNS_VISIBLE } from '../../../../../config/featureFlags'
import { campaignPath } from '../../campaigns/data/open'
import { bookingsBySource, bookingsOverTime, whatWorked, reviewResults, voucherResults, SOURCES } from '../data/results'
import { Kpi, Card, Delta, SourceBar, WeekChart, listOf, plural, moneyOf } from './parts'

const TOP = 5
// Each source as a sentence names it
const SAY = { campaigns: ['campaigns'], sends: ['emails', 'texts'], frontdesk: ['the front desk'], journeys: ['journeys'] }
// The money the server has put against marketing, when it reports it
// Beu's rule (25 July 2026): email = a tap, text = delivered, 14 days.
// The server says which rule it used; 14 until it does.
const ruleDays = overview => (overview && overview.rule && Number(overview.rule.windowDays)) || 14
const bookingMoney = overview => (overview ? (overview.attributedRevenue ?? overview.revenue ?? null) : overview)

// Overview: what marketing brought in, where it came from, what did it
const OverviewTab = ({ data, currency, history, frontDeskOn, demo }) => {
  const { sends, groups, posts, frontDesk, journeys, vouchers: dash, reviews, overview, range, prev, freeTimes } = data
  const money = moneyOf(currency)
  // Counted once every source has answered, so the numbers don't jump
  const ready = sends !== undefined && journeys !== undefined && frontDesk !== undefined
  const fd = frontDesk && frontDesk.used ? frontDesk.conversations : null
  const inputs = useMemo(() => ({ sends: sends || [], groups: CAMPAIGNS_VISIBLE ? groups : null, posts, frontDesk: fd, journeys: journeys || null }), [sends, groups, posts, fd, journeys])
  const vouchers = useMemo(() => voucherResults(dash), [dash])

  const now = useMemo(() => (ready ? bookingsBySource(inputs, range) : null), [ready, inputs, range])
  // The period before, unless the front desk couldn't be read back that far
  const before = useMemo(() => (ready && prev && (!fd || frontDesk.complete) ? bookingsBySource(inputs, prev) : null), [ready, inputs, prev, fd, frontDesk])
  const over = useMemo(() => (ready ? bookingsOverTime(inputs, range) : null), [ready, inputs, range])
  const worked = useMemo(() => (ready ? whatWorked({ ...inputs, vouchers }, range, TOP) : []), [ready, inputs, vouchers, range])
  const fromBookings = bookingMoney(overview)
  const brought = dash === undefined || overview === undefined ? undefined
    : fromBookings == null && !vouchers ? null
      : (Number(fromBookings) || 0) + ((vouchers && vouchers.revenue) || 0)
  const rv = reviews ? reviewResults(reviews, range) : null
  const rvBefore = reviews && prev ? reviewResults(reviews, prev) : null
  const sources = now ? SOURCES.filter(s => now.known.includes(s.id)) : []
  const parts = sources.map(s => ({ id: s.id, label: s.label, value: now.by[s.id] || 0 }))

  const open = (w) => {
    hapticTick()
    if (w.kind === 'campaign') history.push(campaignPath(w.id), { from: '/marketing-performance' })
    else if (w.kind === 'email' || w.kind === 'sms') history.push('/marketing-campaigns', { openCampaignId: w.id })
    else if (w.kind === 'journey') history.push('/marketing-journeys', { openJourneyId: w.id })
    else if (w.kind === 'frontdesk') history.push('/assistant/front-desk')
    else if (w.kind === 'vouchers') history.push('/voucher-v2', { subNav: 'sales' })
  }

  const moneySub = brought == null ? null
    : fromBookings != null && vouchers ? `${money(fromBookings)} from bookings, ${money(vouchers.revenue)} in gift vouchers`
      : vouchers ? `Gift vouchers sold. Money from bookings joins this when each booking records where it came from.`
        : 'From bookings that came from marketing'
  const moneyDelta = fromBookings == null && vouchers && vouchers.previous
    ? <Delta on now={vouchers.revenue} prev={vouchers.previous.revenue} format={money} />
    : null
  // What can't be counted yet
  const pending = [frontDeskOn && frontDesk === null && 'the front desk', journeys === null && 'journeys', 'reminders', 'your link in bio'].filter(Boolean)

  return (
    <div className="pf-tab">
      <div className="pf-kpis">
        <Kpi lead label="Brought in" value={brought === undefined ? undefined : brought === null ? null : money(brought)} sub={moneySub} delta={moneyDelta} pending="Shows when bookings record where they came from" />
        <Kpi label="Bookings from marketing" value={now ? now.total.toLocaleString('en-GB') : undefined} sub={now ? `From ${listOf(sources.flatMap(s => SAY[s.id]))}` : null} delta={now && before ? <Delta now={now.total} prev={before.total} /> : null} />
        <Kpi label="New clients" value={null} pending="Arrives when bookings record where they came from" />
        <Kpi label="New reviews" value={reviews === undefined ? undefined : rv ? rv.count.toLocaleString('en-GB') : null} sub={rv && rv.count ? `${rv.average} average${rv.google ? `, ${rv.google} on Google` : ''}` : null} delta={rv && rvBefore ? <Delta now={rv.count} prev={rvBefore.count} /> : null} pending="Reviews couldn’t be read just now" />
      </div>

      <Card title="Where bookings came from">
        {now ? (
          now.total ? <SourceBar parts={parts} /> : <p className="pf-muted">No bookings came from marketing in these dates yet.</p>
        ) : <span className="pf-skel is-bar" />}
        <p className="pf-foot">Bookings from {listOf(pending)} join this once they can be counted.</p>
      </Card>

      <div className="pf-row2">
        <Card title="What worked">
          {!ready ? <span className="pf-skel is-list" /> : worked.length || (freeTimes && freeTimes.booked) ? (
            <ol className="pf-worked">
              {worked.map((w, i) => (
                <li key={w.key}>
                  <button type="button" onClick={() => open(w)} disabled={demo && (w.kind === 'email' || w.kind === 'sms')}>
                    <em>{i + 1}</em>
                    <span>{w.title}<small>{w.sub}</small></span>
                    <strong>{w.money != null ? money(w.money) : plural(w.bookings, 'booking', 'bookings')}</strong>
                  </button>
                </li>
              ))}
              {freeTimes && freeTimes.booked > 0 && worked.length < TOP && (
                <li>
                  <button type="button" onClick={() => { hapticTick(); history.replace('/marketing-performance?tab=social') }}>
                    <em>{worked.length + 1}</em>
                    <span>Free times posts<small>{`Social · ${plural(freeTimes.posted, 'free time', 'free times')} posted`}</small></span>
                    <strong>{freeTimes.booked} booked</strong>
                  </button>
                </li>
              )}
            </ol>
          ) : <p className="pf-muted">Nothing has brought in bookings in these dates yet. As your marketing does, the best of it shows here.</p>}
        </Card>
        <Card title={over && !over.weekly ? 'Bookings by month' : 'Bookings by week'}>
          {over ? (
            over.buckets.some(b => b.total) ? (
              <>
                <WeekChart buckets={over.buckets} keys={sources.map(s => s.id)} />
                <p className="pf-foot">Bookings from emails and texts sit in the {over.weekly ? 'week' : 'month'} they were sent.</p>
              </>
            ) : <p className="pf-muted">Nothing to chart in these dates yet.</p>
          ) : <span className="pf-skel is-chart" />}
        </Card>
      </div>

      <p className="pf-rule">An email counts the bookings made in the {ruleDays(overview)} days after a client taps one of its links, and a text the bookings made in the {ruleDays(overview)} days after it reaches them (texts can't record a tap). The front desk counts the bookings Bailey made in a conversation, and a journey the bookings it led to. Each booking counts once, to the most recent of these before it was made. Cancellations and no-shows don't count.</p>
    </div>
  )
}

export default OverviewTab
